import { createGoogleGenerativeAI } from "@ai-sdk/google";
import { generateToolSet } from "@builder/assist/services/tools/toolset";
import type { Message, TextStreamPart } from "ai";
import { streamText, type CoreMessage, type StreamTextOnChunkCallback } from "ai";
import { useRef, useState } from "react";
import { v4 } from "uuid";
import { getGeneralSystemPrompt } from "./prompts";



/// IMPORTANT: ALL OF THIS IS LEGACY CODE. NEWSET VERSION USES AI IN THE BACKEND, AUTH AND MANY MORE CHANGES - IN OWN BRANCH FEAT/AI

const googleGenerativeAI = createGoogleGenerativeAI({
    apiKey: import.meta.env.VITE_GOOGLE_API_KEY
});

export const model = googleGenerativeAI("gemini-2.5-pro");


const streamAIResponse = async (
    previousMessages: Message[],
    userText: string,
    imageParts: Array<{ type: 'image'; image: Uint8Array; mimeType: string }>,
    assistantMessageId: string,
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>,
    setSteps: React.Dispatch<React.SetStateAction<TextStreamPart<any>[]>>,
    abortController: AbortController
) => {

    try {
        const maxMessages = 3;

        const recentMessages: CoreMessage[] = [...previousMessages]
            .slice(-(maxMessages * 2))
            .map(({ role, content }) => ({ role, content } as CoreMessage));

        const userContentParts: any[] = [];
        if (userText.trim().length) {
            userContentParts.push({ type: "text", text: userText });
        }
        userContentParts.push(...imageParts);

        // helper to add a single stream part (from either chunk or internal tool streaming)
        const addStep = (part?: TextStreamPart<any>) => {
            if (!part) return;
            setSteps(prev => [...prev, part]);
        };

        // chunk handler for inner tool streams
        const toolOnChunk: StreamTextOnChunkCallback<any> = event => {
            console.log("tool inner onChunk", event);
            addStep(event.chunk);
        };

        const messagesToSendTheAI: CoreMessage[] = [
            {
                role: "system",
                content: getGeneralSystemPrompt()
            } as CoreMessage,
            ...recentMessages,
            {
                role: "user",
                content: userContentParts,
            } as CoreMessage,
        ];

    
        const { textStream } = streamText({
            model: model,
            messages: messagesToSendTheAI,
            tools: generateToolSet({ onChunk: toolOnChunk, abortSignal: abortController.signal }),
            temperature: 0.7,
            topP: 1,
            maxSteps: 20,
            topK: 40,
            
            frequencyPenalty: 0,
            onChunk: chunk => {
                console.log("%cCHUNK:", "color: blue; font-weight: bold;", chunk?.chunk);
                addStep(chunk?.chunk);
            },
            presencePenalty: 0,
            abortSignal: abortController.signal
        });

        // Stream the response directly
        for await (const chunk of textStream) {
            setMessages(prev => 
                prev.map(msg => 
                    msg.id === assistantMessageId 
                        ? { ...msg, content: msg.content + chunk }
                        : msg
                )
            );
        }
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            console.log("Streaming aborted by user");
            setMessages(prev => prev.map(msg => msg.id === assistantMessageId ? { ...msg, content: "aborted by user" } : msg));
            setSteps([]);
        } else {
            console.error("Error in streaming:", error);
            setMessages(prev => 
                prev.map(msg => 
                    msg.id === assistantMessageId 
                        ? { ...msg, content: `I got an error you crap: ${error}` }
                        : msg
                )
            );
        }
    }
};

export const useAiChat = () => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [status, setStatus] = useState<"idle" | "streaming">("idle");
    const [steps, setSteps] = useState<TextStreamPart<any>[]>([]);
    const abortControllerRef = useRef<AbortController | null>(null);

    const abort = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    };
    
    const handleSubmit = async (input: string, attachments: File[] = []) => {
        const trimmedInput = input.trim();
        if (!trimmedInput && attachments.length === 0) return;

        // Abort any existing stream
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const attachmentPreviews = attachments.map(file => ({ url: URL.createObjectURL(file) }));
        const userMessage: Message = {
            id: v4(),
            role: "user",
            content: trimmedInput,
            experimental_attachments: attachmentPreviews
        };

        setMessages(prev => [...prev, userMessage]);
        setStatus("streaming");

        const assistantMessageId = v4();
        const assistantMessage: Message = {
            id: assistantMessageId,
            role: "assistant",
            content: ""
        };

        setMessages(prev => [...prev, assistantMessage]);
        setSteps([{ type: "reasoning", textDelta: "" }]);

        abortControllerRef.current = new AbortController();

        try {
            const imageParts = await Promise.all(
                attachments.map(async file => {
                    const buffer = await file.arrayBuffer();
                    return { type: 'image', image: new Uint8Array(buffer), mimeType: file.type } as const;
                })
            );

            await streamAIResponse(messages, trimmedInput, imageParts, assistantMessageId, setMessages, setSteps, abortControllerRef.current);
        } finally {
            setStatus("idle");
            abortControllerRef.current = null;
        }
    };

    return {
        messages,
        handleSubmit,
        status,
        steps,
        abort
    };
};
